
import type { SynonymTypeConfig } from '@empathybroker/tsclient-play/dist/models/instance/domains/tools/synonym-type-config'
import type { SetEnablePayload } from '../../composables'
import type { CrudListData } from '../../mixins/crud-list.mixin'
import type { DataTableColumn, DataTableOption } from '../../models'
import { ActionTypeEnum } from '@empathybroker/tsclient-play/dist/enums/action-type.enum'
import { defineComponent } from 'vue'
import { mapGetters } from 'vuex'
import CrudList from '../../components/crud/crud-list.component.vue'
import DataTable from '../../components/crud/data-table.component.vue'
import { AlignEnum, FeatureIdEnum, FilterIdEnum } from '../../enums'
import { CrudListMixin } from '../../mixins/crud-list.mixin'
import { ROUTER_CONSTS } from '../../router/router.consts'
import { CONFIG_CONSTS } from '../../store/config/config.consts'
import { buildMessagesWithSeparator } from '../../utils/messages.utils'
import SynonymsForm from '../../views/synonyms/synonyms-form.view.vue'

export default defineComponent({
  name: 'SynonymsList',
  components: {
    CrudList,
    DataTable,
  },
  computed: {
    ...mapGetters(CONFIG_CONSTS.moduleName, {
      instanceConfig: CONFIG_CONSTS.getters.currentInstance,
    }),
    toolConfig() {
      return SynonymsForm.computed.toolConfig.bind(this)()
    },
    tableColumns(): DataTableColumn[] {
      return this.columns.filter((column: DataTableColumn) => {
        if (column.id === 'type') {
          return SynonymsForm.computed.isTypeVisible.bind(this)()
        }
        if (column.id === FilterIdEnum.LANG) {
          return SynonymsForm.computed.isLangVisible.bind(this)()
        }
        return true
      })
    },
  },
  mixins: [CrudListMixin],
  data(): Partial<CrudListData> {
    const messages = this.$epb.messages.tools.synonyms
    const updateDateColumnId = 'meta.updateDate'
    return {
      dataType: ActionTypeEnum.SYNONYM,
      routes: {
        create: ROUTER_CONSTS.routes[FeatureIdEnum.SYNONYM].create,
        update: ROUTER_CONSTS.routes[FeatureIdEnum.SYNONYM].update,
      },
      columns: [
        {
          id: 'type',
          text: messages.fields.type.label,
          sortable: true,
          width: '8rem',
        },
        {
          id: 'lang',
          text: this.$epb.messages.filters.names.lang,
          sortable: true,
        },
        {
          id: 'id',
          text: messages.fields.id.label,
          copyToClipboard: true,
          width: '15rem',
        },
        {
          id: 'synonyms',
          text: messages.fields.synonyms.label,
          width: '200%',
          unwrapText: true,
          copyToClipboard: true,
        },
        {
          id: 'hyponyms',
          text: messages.fields.hyponyms.label,
          unwrapText: true,
          copyToClipboard: true,
        },
        {
          id: updateDateColumnId,
          text: messages.fields.updateDate.label,
          sortable: true,
        },
        {
          id: 'meta.user',
          text: messages.fields.user.label,
          sortable: true,
        },
        {
          id: 'enabled',
          text: messages.fields.enabled.label,
          sortable: true,
        },
        {
          id: 'options',
          width: '44px',
          align: AlignEnum.CENTER,
        },
      ],
      messages,
    }
  },
  methods: {
    getTypeText(type: string): string {
      const typeConfig = this.toolConfig.types.find(
        (typeCfg: SynonymTypeConfig) => typeCfg.id === type,
      )
      if (typeConfig) {
        const typeValuesMessages = this.messages.fields.type.values
        return typeConfig.prettyName || typeValuesMessages[type] || typeConfig.id
      }
      return type
    },
    async setEnabled({ enabled }: SetEnablePayload<any>) {
      this.loadingData = true
      try {
        await this.enabledUpdate(enabled)
      } catch (errors) {
        const conflictingElements = errors.details?.fields.filter(
          error => !!error.conflictingElements,
        )
        if (errors.status === 400 && !!conflictingElements) {
          const conflictErrorsIds: Array<string> = []

          conflictingElements.forEach(error => {
            error.conflictingElements.forEach(conflictingElement => {
              conflictErrorsIds.push(conflictingElement.id)
            })
          })

          const { enableConflictError } = this.messages.errors

          const buildMessage = this.$epb.utils.compileMessage(enableConflictError.message, {
            conflictingElements: buildMessagesWithSeparator(
              conflictErrorsIds,
              ', ',
              this.$epb.messages.app.and,
              true,
            ),
          })

          this.$emit('showCustomError', {
            title: enableConflictError.title,
            message: buildMessage,
          })
        } else {
          this.$emit('showError', this.$epb.utils.decodeErrorMessage(errors))
        }
        this.$emit('updateLoading', { visible: false })
      } finally {
        await this.loadData()
      }
    },
    onOptionsClick(option: DataTableOption, dataItem) {
      this.onOptionClick(option, dataItem, this.$refs.crudListRef)
    },
  },
})
